<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl" class="form-card">
            <!-- Page Header integrated into Form Card -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">
                        Add New Bank Detail
                      </h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Create a new bank account detail with complete
                        information
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Back Button and Form Badge -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="info" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-bank-plus</v-icon>
                      New Bank Detail Form
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="goBackToContext"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- Account Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2"
                      >mdi-account-details</v-icon
                    >
                    Account Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.account_name"
                        :rules="accountNameRules"
                        label="Account Holder Name"
                        prepend-inner-icon="mdi-account"
                        solo
                        rounded
                        required
                        :error-messages="fieldErrors.account_name"
                        @input="clearError('account_name')"
                        placeholder="Enter account holder name"
                        class="professional-field"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.account_number"
                        :rules="accountNumberRules"
                        label="Account Number"
                        prepend-inner-icon="mdi-credit-card"
                        solo
                        rounded
                        required
                        :error-messages="fieldErrors.account_number"
                        @input="clearError('account_number')"
                        placeholder="Enter account number"
                        class="professional-field"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.user_id"
                        :items="users"
                        :rules="userRules"
                        :label="isDoctorContext ? 'Doctor' : 'User (Optional)'"
                        prepend-inner-icon="mdi-account"
                        solo
                        rounded
                        :clearable="!isDoctorContext"
                        :disabled="isDoctorContext"
                        :error-messages="fieldErrors.user_id"
                        @input="clearError('user_id')"
                        :placeholder="isDoctorContext ? 'Linked to current doctor' : 'Select user'"
                        class="professional-field"
                        :menu-props="{ maxHeight: 420 }"
                      ></v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.account_type_id"
                        :items="accountTypes"
                        label="Account Type (Optional)"
                        prepend-inner-icon="mdi-bank"
                        solo
                        rounded
                        clearable
                        :error-messages="fieldErrors.account_type_id"
                        @input="clearError('account_type_id')"
                        placeholder="Select account type"
                        class="professional-field"
                      ></v-autocomplete>
                    </v-col>
                  </v-row>
                </div>

                <!-- Bank Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-bank</v-icon>
                    Bank Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.bank_name"
                        :items="bankNames"
                        :rules="bankNameRules"
                        label="Bank Name"
                        prepend-inner-icon="mdi-bank"
                        solo
                        rounded
                        :error-messages="fieldErrors.bank_name"
                        @input="clearError('bank_name')"
                        placeholder="Select or enter bank name"
                        class="professional-field"
                      ></v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.branch"
                        :rules="branchRules"
                        label="Branch"
                        prepend-inner-icon="mdi-bank-outline"
                        solo
                        rounded
                        :error-messages="fieldErrors.branch"
                        @input="clearError('branch')"
                        placeholder="Enter branch name/location"
                        class="professional-field"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.ifsc_code"
                        :rules="ifscRules"
                        label="IFSC Code"
                        prepend-inner-icon="mdi-code-string"
                        solo
                        rounded
                        :error-messages="fieldErrors.ifsc_code"
                        @input="clearError('ifsc_code')"
                        placeholder="e.g., SBIN0001234"
                        class="professional-field"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.micr_code"
                        :rules="micrRules"
                        label="MICR Code"
                        prepend-inner-icon="mdi-numeric"
                        solo
                        rounded
                        :error-messages="fieldErrors.micr_code"
                        @input="clearError('micr_code')"
                        placeholder="Enter 9-digit MICR code"
                        class="professional-field"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Status Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2"
                      >mdi-toggle-switch</v-icon
                    >
                    Status Settings
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-card outlined class="status-card">
                        <v-card-text class="pa-2">
                          <div class="d-flex align-center">
                            <v-icon color="grey" class="mr-3"
                              >mdi-bank-check</v-icon
                            >
                            <div class="flex-grow-1">
                              <div class="font-weight-medium mb-1">
                                Account Status
                              </div>
                              <div class="text-caption grey--text">
                                Set the status of this bank account
                              </div>
                            </div>
                            <v-switch
                              v-model="form.status"
                              color="success"
                              inset
                              hide-details
                              class="ml-3"
                            ></v-switch>
                          </div>
                          <div class="mt-2">
                            <v-chip
                              small
                              :color="form.status ? 'success' : 'warning'"
                              :text-color="form.status ? 'white' : 'black'"
                            >
                              {{
                                form.status
                                  ? "Active Account"
                                  : "Inactive Account"
                              }}
                            </v-chip>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                  </v-row>
                </div>
              </v-form>
            </v-card-text>

            <!-- Form Actions Footer -->
            <v-divider></v-divider>
            <v-card-actions class="form-actions pa-2">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip
                      small
                      :color="hasValidationErrors ? 'error' : 'success'"
                      :text-color="hasValidationErrors ? 'white' : 'white'"
                      class="mr-3"
                    >
                      <v-icon left x-small>
                        {{
                          hasValidationErrors
                            ? "mdi-alert-circle"
                            : "mdi-check-circle"
                        }}
                      </v-icon>
                      {{
                        hasValidationErrors
                          ? "Please fix validation errors"
                          : "Form validation passed"
                      }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      All required fields must be completed
                    </span>
                  </div>
                </v-col>

                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex justify-end">
                    <v-btn
                      color="grey"
                      outlined
                      class="mr-3 action-button"
                      @click="resetForm"
                      :disabled="isSaving"
                    >
                      <v-icon left>mdi-refresh</v-icon>
                      Reset
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="action-button"
                      @click="submitForm"
                      :loading="isSaving"
                      :disabled="isSaving || !valid"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Create Bank Detail
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Success Dialog -->
      <v-dialog v-model="showSuccessDialog" width="600">
        <v-card>
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-check-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Bank detail created successfully.</h3>
                      <br />
                      <v-btn color="success" @click="navigateToList">
                        View Bank Details
                      </v-btn>
                      <v-btn
                        color="primary"
                        text
                        @click="createAnother"
                        class="ml-2"
                      >
                        Create Another
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from 'axios';

export default {
  name: "BankDetailCreate",
  data() {
    return {
      valid: false,
      isSaving: false,
      showSuccessDialog: false,

      form: {
        user_id: null,
        account_name: "",
        account_number: "",
        bank_name: "",
        branch: "",
        ifsc_code: "",
        micr_code: "",
        account_type_id: null,
        status: false,
        is_active: true,
      },

      fieldErrors: {},

      // Form options
      users: [],
      accountTypes: [],
      bankNames: [
        { text: "State Bank of India", value: "State Bank of India" },
        { text: "HDFC Bank", value: "HDFC Bank" },
        { text: "ICICI Bank", value: "ICICI Bank" },
        { text: "Axis Bank", value: "Axis Bank" },
        { text: "Punjab National Bank", value: "Punjab National Bank" },
        { text: "Bank of Baroda", value: "Bank of Baroda" },
        { text: "Canara Bank", value: "Canara Bank" },
        { text: "Union Bank of India", value: "Union Bank of India" },
        { text: "Indian Bank", value: "Indian Bank" },
        { text: "Central Bank of India", value: "Central Bank of India" },
        { text: "IDBI Bank", value: "IDBI Bank" },
        { text: "Indian Overseas Bank", value: "Indian Overseas Bank" },
      ],

      // Validation rules
      accountNameRules: [
        (v) => !!v || "Account holder name is required",
        (v) =>
          (v && v.length <= 100) ||
          "Account name must be less than 100 characters",
      ],
      accountNumberRules: [
        (v) => !!v || "Account number is required",
        (v) =>
          (v && v.length <= 100) ||
          "Account number must be less than 100 characters",
        (v) => /^[0-9]+$/.test(v) || "Account number must contain only numbers",
      ],
      userRules: [],
      bankNameRules: [
        (v) => !!v || "Bank name is required",
        (v) =>
          (v && v.length <= 100) ||
          "Bank name must be less than 100 characters",
      ],
      branchRules: [
        (v) =>
          !v ||
          (v && v.length <= 100) ||
          "Branch name must be less than 100 characters",
      ],
      ifscRules: [
        (v) =>
          !v ||
          (v && v.length <= 100) ||
          "IFSC code must be less than 100 characters",
        (v) =>
          !v ||
          /^[A-Z]{4}0[A-Z0-9]{6}$/.test(v) ||
          "Invalid IFSC format (e.g., SBIN0001234)",
      ],
      micrRules: [
        (v) =>
          !v ||
          (v && v.length <= 100) ||
          "MICR code must be less than 100 characters",
        (v) =>
          !v || /^[0-9]{9}$/.test(v) || "MICR code must be exactly 9 digits",
      ],
    };
  },

  computed: {
    hasValidationErrors() {
      return Object.keys(this.fieldErrors).length > 0 || !this.valid;
    },
    isDoctorContext() {
      return (
        !!this.$route.params.doctorId ||
        this.$route.name === 'DoctorBankDetailCreatePage' ||
        /\/doctors\/[^/]+\/bank-details/.test(this.$route.path)
      );
    },
    doctorId() {
      return this.isDoctorContext ? this.$route.params.doctorId : null;
    },
  },

  async mounted() {
    await this.loadMasterData();
    if (this.isDoctorContext) {
      await this.preselectDoctor();
    }
  },

  methods: {
    async loadMasterData() {
      try {
        const masters = await axios.get("/masters", {
          params: { context: "bank_details" }
        });
        
        if (masters.data.users) {
          this.users = masters.data.users.map((user) => {
            const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.user_name || 'Unknown';
            const label = user.email ? `${name} (${user.email})` : name;
            return { text: label, value: user.id };
          });
        }
        
        if (masters.data.account_types) {
          this.accountTypes = masters.data.account_types.map((type) => ({
            text: type.description,
            value: type.id,
          }));
        }
      } catch (error) {
        console.log("Error loading master data:", error);
        // Fallback to basic account types
        this.accountTypes = [
          { text: "Savings Account", value: 1 },
          { text: "Current Account", value: 2 },
          { text: "Fixed Deposit", value: 3 },
          { text: "Recurring Deposit", value: 4 },
        ];
      }
    },

    clearError(field) {
      if (this.fieldErrors[field]) {
        this.$delete(this.fieldErrors, field);
      }
    },

    async preselectDoctor() {
      try {
        const response = await axios.get(`/users/${this.doctorId}`);
        const doctor = response.data?.data || response.data;
        if (!doctor) return;
        const name = `${doctor.first_name || ''} ${doctor.last_name || ''}`.trim();
        const label = doctor.email ? `${name} (${doctor.email})` : name;
        // Ensure the doctor is in the users dropdown so the selected value resolves
        if (!this.users.find(u => u.value === doctor.id)) {
          this.users.unshift({ text: label, value: doctor.id });
        }
        this.form.user_id = doctor.id;
      } catch (err) {
        console.warn('Could not preselect doctor:', err);
      }
    },

    goBackToContext() {
      this.$router.back();
    },

    async submitForm() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.isSaving = true;
      this.fieldErrors = {};

      try {
        const response = await axios.post("/bank_details", this.form);

        if (response.data.success) {
          this.showSuccessDialog = true;
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              response.data.message ||
              "An error occurred while creating the bank detail.",
          });
        }
      } catch (error) {
        console.error(error);

        if (error.response) {
          if (error.response.status === 422) {
            this.fieldErrors = error.response.data.errors || {};
          } else if (error.response.status === 401) {
            this.$router.push("/login");
          } else {
            this.$store.dispatch("errors/setError", {
              message:
                error.response.data.message ||
                "An error occurred while creating the bank detail.",
            });
          }
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              "Network error. Please check your connection and try again.",
          });
        }
      } finally {
        this.isSaving = false;
      }
    },

    resetForm() {
      this.$refs.form.reset();
      this.form = {
        user_id: null,
        account_name: "",
        account_number: "",
        bank_name: "",
        branch: "",
        ifsc_code: "",
        micr_code: "",
        account_type_id: null,
        status: false,
        is_active: true,
      };
      this.fieldErrors = {};
    },

    navigateToList() {
      this.showSuccessDialog = false;
      this.$router.back();
    },

    createAnother() {
      this.showSuccessDialog = false;
      this.resetForm();
      // In doctor context, keep the doctor selected after reset
      if (this.isDoctorContext) {
        this.preselectDoctor();
      }
    },
  },
};
</script>

<style scoped>
/* Professional Form Card */
.form-card {
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
}

.page-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.page-subtitle {
  font-size: 0.875rem;
  margin: 0;
}

.back-button {
  min-width: 120px;
  height: 38px;
  font-weight: 500;
  text-transform: none;
}

.form-content {
  background: #ffffff;
}

/* Section Styling */
.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

/* Professional Form Fields */
.professional-field >>> .v-input__control {
  min-height: 48px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 24px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.875rem;
  padding-top: 12px;
  padding-bottom: 12px;
}

.professional-field >>> .v-label {
  font-size: 0.875rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

/* Status Card */
.status-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9fa;
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 140px;
  height: 42px;
  font-weight: 500;
  text-transform: none;
  border-radius: 6px;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-title {
    font-size: 1.25rem;
  }

  .form-content {
    padding: 1rem !important;
  }

  .back-button {
    min-width: 100px;
    height: 36px;
  }

  .action-button {
    min-width: 120px;
    height: 38px;
  }
}

@media (max-width: 600px) {
  .page-title {
    font-size: 1.1rem;
  }

  .form-content {
    padding: 0.75rem !important;
  }

  .section-title {
    font-size: 0.9rem;
  }

  .professional-field >>> .v-input__control {
    min-height: 44px;
  }

  .back-button {
    min-width: 90px;
    height: 34px;
  }

  .action-button {
    min-width: 100px;
    height: 36px;
  }
}

/* Clean Focus States */
.professional-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

/* Consistent Margins */
.mb-1 {
  margin-bottom: 0.5rem !important;
}

.mb-2 {
  margin-bottom: 0.75rem !important;
}

/* Professional Transitions */
.v-card,
.v-btn,
.professional-field,
.status-card {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>